import { createContext, memo, useContext } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import remarkAiDirectives from './utils/remarkAiDirectives';
import maskIncompleteDirective from './utils/maskIncompleteDirective';
import unwrapBacktickedDirectives from './utils/unwrapBacktickedDirectives';
import DocReference from './DocReference';
import styles from './AiAssistant.module.scss';

type Props = {
  content: string;
  // True while the assistant turn is still streaming — directives render as
  // placeholders and skip fetching until the message is persisted, and a
  // half-delivered trailing directive is hidden until its closing brace lands.
  isStreaming?: boolean;
  // Render markdown WITHOUT the `:documentation{id}` directive layer — the text
  // stays literal. Used for the Thoughts panel: reasoning is the model's own
  // monologue where it often quotes the chip syntax verbatim (e.g. "using
  // `:documentation{id=...}`"), and unwrapping/parsing those into live chips is
  // wrong there. The answer bubble keeps the directive layer (default false).
  plain?: boolean;
};

// react-markdown renders to React elements (no dangerouslySetInnerHTML) and
// does NOT render raw HTML unless rehype-raw is added — so model output stays
// XSS-safe by default. Links are forced to open in a new tab with noopener.
//
// remark-directive parses `:documentation{id=x}` / `::documentation{id=x}` into
// directive nodes, remarkAiDirectives rewrites the known ones to the
// `ai-doc-ref` element, and the component map below renders them inline (chip)
// or as a block card.
const remarkPlugins = [remarkGfm, remarkDirective, remarkAiDirectives];
// Plain variant (Thoughts panel): GFM only, so `:documentation{id}` is left as
// literal text instead of being parsed into a directive node and swapped for a
// chip.
const plainRemarkPlugins = [remarkGfm];

// Carries the streaming flag to the (module-level) ai-doc-ref renderer without
// re-creating the components map on every render.
const StreamingContext = createContext(false);

function MarkdownLink({
  children,
  // react-markdown passes the hast `node` to every component; pull it out of the
  // rest so it isn't forwarded onto the DOM <a> (React warns on the unknown
  // attribute). Intentionally unused — kept as a rest-sibling to strip it.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  node,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

// Render paragraphs as a <div>, not <p>: an inline `:documentation{id}` chip can
// embed block-level content (its ActionsMenu renders <div>s), and a <div> inside
// a <p> is invalid HTML that React flags as a hydration error. A div paragraph
// carries the same block spacing via `.markdownParagraph`.
function MarkdownParagraph({
  children,
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <div className={styles.markdownParagraph}>{children}</div>;
}

// GFM tables come straight from the model, which picks both the column count and
// how verbose each cell is — so a table can easily want more width than the
// bubble has. Wrapping it in its own scroller lets a genuinely wide table scroll
// horizontally instead of having its columns crushed down to a couple of
// characters to fit (see `.markdownTableWrap` / the cell sizing rules).
function MarkdownTable({
  children,
  // Same as MarkdownLink: strip the hast `node` so it isn't forwarded to the DOM.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  node,
  ...props
}: React.TableHTMLAttributes<HTMLTableElement> & { node?: unknown }) {
  return (
    <div className={styles.markdownTableWrap}>
      <table {...props}>{children}</table>
    </div>
  );
}

function AiDocRef({
  id,
  versionId,
  inline,
}: {
  id?: string;
  versionId?: string;
  inline?: boolean | string;
}) {
  const isStreaming = useContext(StreamingContext);
  return (
    <DocReference
      id={id}
      versionId={versionId || undefined}
      inline={inline === true || inline === 'true'}
      isStreaming={isStreaming}
    />
  );
}

const components = {
  a: MarkdownLink,
  p: MarkdownParagraph,
  table: MarkdownTable,
  'ai-doc-ref': AiDocRef,
} as Components;

function MessageMarkdown({
  content,
  isStreaming = false,
  plain = false,
}: Props) {
  // Plain mode leaves directive syntax untouched (no unwrap, no masking) so it
  // renders as literal text. Otherwise: un-code-format directives the model
  // backticked before anything parses them, then (while streaming) hide a
  // trailing half-delivered one.
  let markdown = content;
  if (!plain) {
    const unwrapped = unwrapBacktickedDirectives(content);
    markdown = isStreaming ? maskIncompleteDirective(unwrapped) : unwrapped;
  }
  return (
    <div className={styles.markdown}>
      <StreamingContext.Provider value={isStreaming}>
        <ReactMarkdown
          remarkPlugins={plain ? plainRemarkPlugins : remarkPlugins}
          components={components}
        >
          {markdown}
        </ReactMarkdown>
      </StreamingContext.Provider>
    </div>
  );
}

// react-markdown runs a full parse + mdast→hast + React reconciliation on every
// render with no internal memoization, so without this every settled message in
// the transcript re-parses its markdown on every streamed delta of the current
// turn (O(messages) parses per frame). Props are primitives, so the default
// shallow compare is exactly right: a settled bubble bails out, and the one
// streaming bubble still re-renders because its `content` grows each frame.
export default memo(MessageMarkdown);
